import { useUserStore } from "@/auth/stores/user";
import Page from "@/components/Page";
import PageContent from "@/components/PageContent";
import PageHeader from "@/components/PageHeader";
import PageTitle from "@/components/PageTitle";
import { Button, message, Space } from "antd";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import styled from "@emotion/styled";
import { colors, font, spacing } from "@/config/theme";
import {
  ArrowLeftOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import useRole from "@/auth/hooks/useRole";
import {
  useChangeTimesheetWeekStatus,
  useGetTimesheetByUserId,
  useSaveTimesheet,
} from "@/time-tracking/api";
import Loading from "@/components/Loading";
import {
  TimesheetCardData,
  TimesheetEntry,
  TimesheetOfWeek,
} from "@/time-tracking/types/Timesheet";
import TimesheetStatusTag from "@/time-tracking/components/TimesheetStatusTag";
import { formatMillisecondsToTime } from "@/time-tracking/utils/time";
import TimeTrackingCard from "@/time-tracking/components/TimeTrackingCard";
import withTimesheetProvider from "@/time-tracking/store/timesheetProvider";
import { useTimesheetData } from "@/time-tracking/store/timesheet";

const SubTitle = styled.span`
  font-size: ${font.size[16]};
  font-weight: 600;
  color: ${colors.gray[800]};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 580px;
`;

const WeekControlWrapper = styled.div`
  display: flex;
  gap: ${spacing.xxs};
  margin-bottom: ${spacing.xs};
`;

const WeekHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const WeekHeaderLabel = styled.span`
  font-size: ${font.size[20]};
  font-weight: 860;
  color: ${colors.gray[800]};
`;

const WeekHeaderValue = styled(WeekHeaderLabel)`
  margin-left: ${spacing.xxs};
  font-weight: 590;
`;

const TimeNumber = styled.span`
  font-size: ${font.size[16]};
  font-weight: 600;
  color: ${colors.daybreakBlue[600]};
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  margin-top: ${spacing.xs};
`;

const UserInfoHeader = ({ user }: { user: TimesheetOfWeek["user"] }) => {
  return (
    <>
      <span>{user.Name}</span>
      <SubTitle>
        {user.Location} | {user.Role}
      </SubTitle>
    </>
  );
};

const Timesheet = () => {
  const { user } = useUserStore();
  const { isAdmin } = useRole();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  const { userId } = useParams();

  const [selectedUser, setSelectedUser] = useState<
    TimesheetOfWeek["user"] | null
  >(null);
  const [selectedDateTimestamp, setSelectedDateTimestamp] = useState(
    dayjs().startOf("week").add(3, "day").toISOString()
  );

  const { data, isLoading } = useGetTimesheetByUserId(
    Number(userId),
    selectedDateTimestamp
  );

  const { timesheetData, setTimesheetData } = useTimesheetData();
  const { mutateAsync: saveTimesheet, isLoading: isSaving } = useSaveTimesheet(
    Number(userId)
  );

  const isApproved = data?.timesheet?.status === "APPROVED";

  const { mutateAsync: changeWeekStatus, isLoading: isUpdatingStatus } =
    useChangeTimesheetWeekStatus(Number(userId));

  useEffect(() => {
    // Redirect non-admin users to their own timesheet page
    if (!isAdmin && user?.UserId !== Number(userId)) {
      navigate(`/time-tracking/timesheet/${user?.UserId}`, { replace: true });
    }
  }, [userId, user, navigate, isAdmin]);

  useEffect(() => {
    if (data) {
      setSelectedUser(data.user);

      // Update timesheet data in context store
      const cardsData: Record<string, TimesheetCardData> = {};

      // Generate 7 days cards for the week
      for (let i = 0; i < 7; i += 1) {
        const currentDate = dayjs.utc(data.startOfWeek).add(i, "day");
        cardsData[currentDate.format("YYYY-MM-DD")] = {
          date: currentDate.toISOString(),
          entries: [],
        };
      }

      data.entries.forEach((entry) => {
        const entryDate = dayjs(entry.date).format("YYYY-MM-DD");
        const targetCard = cardsData[entryDate];
        if (targetCard) {
          targetCard.entries.push(entry);
        }
      });

      setTimesheetData(cardsData);
    }
  }, [data, setTimesheetData]);

  const getTotalTrackedTime = useMemo(() => {
    const totalMilliseconds = Object.values(timesheetData).reduce(
      (accCard, card) => {
        const cardTotal = card.entries.reduce((accEntry, entry) => {
          return accEntry + Number(entry.time ?? 0);
        }, 0);
        return accCard + cardTotal;
      },
      0
    );

    return formatMillisecondsToTime(totalMilliseconds);
  }, [timesheetData]);

  const handleGoBack = () => {
    navigate("/time-tracking");
  };

  const handleSaveClick = async () => {
    const allEntries: TimesheetEntry[] = [];
    Object.values(timesheetData).forEach((card) => {
      allEntries.push(...card.entries);
    });

    // Identify entries to delete (those with numeric IDs that are missing)
    const existingEntryIds = new Set(
      data?.entries
        .map((entry) => entry.id)
        .filter((id) => typeof id === "number") as number[]
    );
    const currentEntryIds = new Set(
      allEntries
        .map((entry) => entry.id)
        .filter((id) => typeof id === "number") as number[]
    );
    const entriesToDelete = Array.from(existingEntryIds).filter(
      (id) => !currentEntryIds.has(id)
    );

    const sanitizedEntries = allEntries.map((entry) => ({
      date: entry.date,
      type: entry.type,
      hours: entry.hours,
      minutes: entry.minutes,
      id: typeof entry.id === "string" ? undefined : entry.id,
    }));

    try {
      await saveTimesheet({
        date: selectedDateTimestamp,
        entries: sanitizedEntries,
        deleteEntries: entriesToDelete,
      });
    } catch (error) {
      messageApi.error("Failed to save timesheet");
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const handleStatusChange = async (status: "APPROVED" | "PENDING") => {
    try {
      await changeWeekStatus({
        timesheetId: data?.timesheet?.id as number,
        status,
      });
    } catch (error) {
      messageApi.error("Failed to update timesheet status");
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  return (
    <Page>
      <PageHeader withTabs={false}>
        {isAdmin && (
          <Button
            type="text"
            onClick={handleGoBack}
            icon={<ArrowLeftOutlined />}
          />
        )}
        <PageTitle
          title={
            selectedUser !== null ? (
              <UserInfoHeader user={selectedUser} />
            ) : (
              "Time Tracking"
            )
          }
        >
          <Space>
            {!isApproved && (
              <Button
                loading={isSaving}
                type="primary"
                onClick={handleSaveClick}
              >
                Save
              </Button>
            )}
            {isAdmin && (
              <Button
                loading={isUpdatingStatus}
                type="primary"
                onClick={() =>
                  handleStatusChange(isApproved ? "PENDING" : "APPROVED")
                }
                disabled={data?.entries.length === 0 && !isApproved}
              >
                {isApproved ? "Edit" : "Approve Week"}
              </Button>
            )}
          </Space>
        </PageTitle>
      </PageHeader>
      <PageContent>
        {isLoading && <Loading />}
        {data && (
          <Wrapper>
            <WeekControlWrapper>
              <Button
                icon={<LeftOutlined />}
                onClick={() =>
                  setSelectedDateTimestamp((prev) =>
                    dayjs(prev).subtract(7, "day").toISOString()
                  )
                }
              />
              <Button
                icon={<RightOutlined />}
                onClick={() =>
                  setSelectedDateTimestamp((prev) =>
                    dayjs(prev).add(7, "day").toISOString()
                  )
                }
              />
            </WeekControlWrapper>
            <WeekHeader>
              <div>
                <WeekHeaderLabel>Current week:</WeekHeaderLabel>{" "}
                <WeekHeaderValue>
                  {`${dayjs.utc(data.startOfWeek).format("DD MMM")} - ${dayjs
                    .utc(data.endOfWeek)
                    .format("DD MMM YYYY")}`}
                </WeekHeaderValue>
              </div>
              <div>
                <TimesheetStatusTag
                  status={data.timesheet?.status ?? "PENDING"}
                />
              </div>
            </WeekHeader>
            <SubTitle>
              Total Time tracked:{" "}
              <TimeNumber>{getTotalTrackedTime}h</TimeNumber>
            </SubTitle>
            <CardsContainer>
              {Object.keys(timesheetData).map((date) => (
                <TimeTrackingCard
                  key={date}
                  date={date}
                  disableEditing={isApproved}
                />
              ))}
            </CardsContainer>
          </Wrapper>
        )}
        {contextHolder}
      </PageContent>
    </Page>
  );
};

export default withTimesheetProvider(Timesheet);
